import { Logo, LogoMark } from "@/components/branding/Logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { dashboardService, SidebarCountersResponse } from "@/services/dashboard.service";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  Book,
  BriefcaseIcon,
  Building2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  PhoneCall,
  Settings,
  TicketIcon,
  UserCircle,
  UserCog,
  UserPlus
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { authService } from "@/services/authService";

type SidebarLink = {
  name: string;
  href: string;
  icon: React.ElementType;
  onClick?: () => void;
  /** Chave do contador (badge) a mostrar nesse item, quando houver. */
  countKey?: keyof SidebarCountersResponse;
};

const mainLinks: SidebarLink[] = [
  { name: "Dashboard", href: "/dashboard", icon: Home },
  { name: "Tickets", href: "/tickets", icon: TicketIcon, countKey: "openTickets" },
  { name: "WhatsApp", href: "/conversations", icon: MessageCircle, countKey: "unreadConversations" },
  { name: "Empresas", href: "/companies", icon: Building2 },
  { name: "Pessoas", href: "/clients", icon: BriefcaseIcon },
  { name: "Canais", href: "/channels", icon: PhoneCall },
  { name: "Equipes", href: "/teams", icon: UserPlus },
  { name: "Agentes", href: "/agents", icon: UserCog },
  { name: "Base de Conhecimento", href: "/knowledge-base", icon: Book },
  { name: "Relatório por Cliente", href: "/reports/tickets-por-cliente", icon: BarChart3 },
];

const bottomLinks: SidebarLink[] = [
  { name: "Assinatura", href: "/assinatura", icon: CreditCard },
  { name: "Perfil", href: "/profile", icon: UserCircle },
  { name: "Configurações", href: "/settings", icon: Settings },
  { name: "Sair", href: "/login", icon: LogOut, onClick: () => authService.logout() },
];

// Links que aparecem na navegação inferior mobile
const mobileNavLinks: SidebarLink[] = [
  { name: "Home", href: "/dashboard", icon: Home },
  { name: "Tickets", href: "/tickets", icon: TicketIcon },
  { name: "Clientes", href: "/clients", icon: BriefcaseIcon },
  { name: "Perfil", href: "/profile", icon: UserCircle },
];

export default function Sidebar() {
  const [expanded, setExpanded] = useState(true);
  const [counters, setCounters] = useState<SidebarCountersResponse | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      dashboardService
        .getCounters()
        .then((data) => {
          if (!cancelled) setCounters(data);
        })
        .catch(() => {});
    };
    load();
    const id = window.setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Função para obter o título da página atual
  const getCurrentPageTitle = () => {
    const currentLink = [...mainLinks, ...bottomLinks, ...mobileNavLinks].find(
      link => link.href === location.pathname
    );
    return currentLink?.name || "Dashboard";
  };

  // Renderiza os links da sidebar
  const SidebarLinks = () => (
    <>
      <div className="flex flex-col flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {mainLinks.map((link) => (
          <TooltipProvider key={link.name} delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={link.href}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
                    location.pathname === link.href
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-sidebar-accent text-sidebar-foreground",
                    !expanded && "justify-center"
                  )}
                >
                  <span className="relative shrink-0">
                    <link.icon className="h-5 w-5 min-w-5" />
                    {!expanded && !!link.countKey && !!counters?.[link.countKey] && (
                      <span className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-destructive" />
                    )}
                  </span>
                  <span
                    className={cn(
                      "flex-1 whitespace-nowrap transition-all duration-300",
                      !expanded && "hidden"
                    )}
                  >
                    {link.name}
                  </span>
                  {expanded && !!link.countKey && !!counters?.[link.countKey] && (
                    <Badge variant="secondary" className="ml-auto shrink-0 px-1.5 py-0 text-[10px]">
                      {counters[link.countKey]}
                    </Badge>
                  )}
                </Link>
              </TooltipTrigger>
              {!expanded && (
                <TooltipContent side="right" className="bg-sidebar border-border">
                  <p>{link.name}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      <div className="flex flex-col px-2 py-4 space-y-1">
        {bottomLinks.map((link) => (
          <TooltipProvider key={link.name} delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={link.href}
                  onClick={(e) => {
                    if (link.onClick) {
                      e.preventDefault();
                      link.onClick();
                    }
                  }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
                    location.pathname === link.href
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-sidebar-accent text-sidebar-foreground",
                    !expanded && "justify-center"
                  )}
                >
                  <link.icon className="h-5 w-5 min-w-5" />
                  <span
                    className={cn(
                      "whitespace-nowrap transition-all duration-300",
                      !expanded && "hidden"
                    )}
                  >
                    {link.name}
                  </span>
                </Link>
              </TooltipTrigger>
              {!expanded && (
                <TooltipContent side="right" className="bg-sidebar border-border">
                  <p>{link.name}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </>
  );

  return (
    <>
      {/* Sidebar para desktop */}
      <aside
        className={cn(
          "hidden lg:flex flex-col h-screen bg-sidebar sticky top-0 border-r border-border transition-all duration-300",
          expanded ? "w-64" : "w-16"
        )}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2 overflow-hidden">
            <LogoMark size={28} />
            <span
              className={cn(
                "text-xl font-bold whitespace-nowrap transition-all duration-300",
                expanded ? "w-auto opacity-100" : "w-0 opacity-0"
              )}
            >
              <span className="text-primary">Baita</span>
              <span className="text-sidebar-accent-foreground">Ticket</span>
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setExpanded(!expanded)}
            className="rounded-full hover:bg-sidebar-accent"
          >
            {expanded ? (
              <ChevronLeft className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </Button>
        </div>

        <SidebarLinks />
      </aside>

      {/* Layout mobile */}
      <div className="lg:hidden flex flex-col min-h-screen">
        {/* Header mobile */}
        <header className="fixed top-0 left-0 right-0 bg-background border-b border-border z-50">
          <div className="flex items-center justify-between h-14 px-4">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80vw] p-0">
                <div className="flex flex-col h-full bg-sidebar">
                  <div className="flex items-center p-4 border-b border-border">
                    <Logo size={26} onDark />
                  </div>
                  <SidebarLinks />
                </div>
              </SheetContent>
            </Sheet>
            <Logo size={26} />
          </div>
        </header>

        {/* Bottom navigation */}
        <nav className="fixed bottom-0 left-0 right-0 border-t border-border z-50 bg-background">
          <div className="grid grid-cols-4 h-16">
            {mobileNavLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={cn(
                  "flex flex-col items-center justify-center gap-1",
                  location.pathname === link.href
                    ? "text-primary"
                    : "text-sidebar-foreground"
                )}
              >
                <link.icon className="h-5 w-5" />
                <span className="text-xs">{link.name}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}
